/* R796 Battlegrounds page: shared pieces of the Obsidian Quiet round 2 look.
   h()          one DOM builder (text is always textContent, never HTML)
   icon()       the Q2 icon set (24 grid, stroke 1.5), plus coin, crystal, ticket, star
   portrait()   every character portrait is SQUARE (Jan, 2026-09-22); rarity is the
                ring, and a Mythic FramedFantasy portrait wears the pink and teal foil
   fmt(), odds() numbers as the server gave them, formatted and never recomputed
   motion       enter/leave/sheet animations, 180-320 ms ease-out, opacity only
                under reduced motion (Jan's rule: nothing ever changes abruptly)
   sheet(), toast(), burst()  the one sheet, the one toast and subtle capped
                particles (none under reduced motion) */

/* pictures that failed to load this visit: a repaint draws the silhouette straight away (imgGone) */
const GONE_IMG = new Set();
export function imgGone(src) { return !!src && GONE_IMG.has(String(src)); }
/* Discourage casual artwork saving on this page, including later-mounted battle and encounter portraits.
   This is not DRM: visible images can still be copied. Links and explicit Export actions keep working. */
if (typeof document !== 'undefined') {
  const guardArtwork = e => {
    const image = e.target;
    if (image && image.tagName === 'IMG' && image.closest('.bg-app,#hoh-contract-overlay')) e.preventDefault();
  };
  document.addEventListener('contextmenu', guardArtwork);
  document.addEventListener('dragstart', guardArtwork);
  /* a Hall picture that no longer loads (a moved or deleted file) never shows the browser's broken image and its alt
     text: a portrait wears the quiet silhouette a hero without a picture wears (its name stays its label), a reserve
     tile or the stage's backlight simply goes without */
  document.addEventListener('error', e => {
    const img = e.target;
    if (!img || img.tagName !== 'IMG' || !img.closest || !img.closest('.bg-app')) return;
    if (img.getAttribute('src')) GONE_IMG.add(img.getAttribute('src'));
    if (img.classList.contains('bg-por__img')) {
      const n = Math.max(16, Math.round((parseFloat(img.getAttribute('width')) || 60) * .42)), alt = img.getAttribute('alt');
      img.replaceWith(h('span', { class: 'bg-por__initial', role: alt ? 'img' : null, 'aria-label': alt || null, 'aria-hidden': alt ? null : 'true' }, icon('heroes', n, null, 1.4)));
    } else if (img.closest('.bg-rtile') || img.classList.contains('bg-hstage__lit')) img.remove();
  }, true);
}

export const RARITY = Object.freeze([
  Object.freeze({ id: 'common', word: 'Common', c: '#8A8090', g: 'rgba(138,128,144,.30)' }),
  Object.freeze({ id: 'uncommon', word: 'Uncommon', c: '#6BCB77', g: 'rgba(107,203,119,.40)' }),
  Object.freeze({ id: 'rare', word: 'Rare', c: '#5B8EC4', g: 'rgba(91,142,196,.48)' }),
  Object.freeze({ id: 'epic', word: 'Epic', c: '#B46BFF', g: 'rgba(180,107,255,.48)' }),
  Object.freeze({ id: 'legendary', word: 'Legendary', c: '#E8C674', g: 'rgba(232,198,116,.46)' }),
  Object.freeze({ id: 'mythic', word: 'Mythic', c: '#EF94FF', g: 'rgba(239,148,255,.40)' })
]);
export function rarityOf(r) {
  if (typeof r === 'string') { const i = RARITY.findIndex(x => x.id === r); return RARITY[i < 0 ? 0 : i]; }
  const n = Number(r);
  return RARITY[Number.isFinite(n) ? Math.max(0, Math.min(5, n | 0)) : 0];
}
export function rarityIndex(r) { return RARITY.indexOf(rarityOf(r)); }

/* ---------------- DOM ---------------- */
export function h(tag, attrs, ...kids) {
  const node = document.createElement(tag);
  if (attrs) {
    for (const k of Object.keys(attrs)) {
      const v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'text') node.textContent = String(v);
      else if (k === 'class') node.className = v;
      else if (k === 'style' && typeof v === 'object') {
        /* custom properties (--x) only take through setProperty */
        for (const p of Object.keys(v)) { if (v[p] == null) continue; if (p.charAt(0) === '-') node.style.setProperty(p, String(v[p])); else node.style[p] = v[p]; }
      }
      else if (k === 'on') { for (const ev of Object.keys(v)) node.addEventListener(ev, v[ev]); }
      else if (k === 'html') node.innerHTML = v;   /* only ever given the icon SVG strings below */
      else if (k === 'dataset') { for (const d of Object.keys(v)) if (v[d] != null) node.dataset[d] = String(v[d]); }
      else node.setAttribute(k, v === true ? '' : String(v));
    }
  }
  append(node, kids);
  return node;
}
function append(node, kids) {
  for (const c of kids) {
    if (c == null || c === false) continue;
    if (Array.isArray(c)) append(node, c);
    else node.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}
/* an element from a trusted SVG string (the icon set below; never player text) */
export function svg(markup) {
  const t = document.createElement('template');
  t.innerHTML = markup.trim();
  return t.content.firstElementChild;
}
export function vh(text) { return h('span', { class: 'bg-vh', text: text }); }
/* a length in page units (BGLAYOUT): n px on phones and tablets, scaled with the window on a desktop
   (the section's --u). Every inline size goes through here so it grows with the CSS around it. */
export function px(n) { return 'calc(' + n + '*var(--u,1px))'; }
function svgSize(s) { return ' style="width:' + px(s) + ';height:' + px(s) + '"'; }
export function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); return node; }

/* ---------------- icons (Q2 set, drawn on a 24 grid) ---------------- */
const P = {
  calendar: '<rect x="4" y="5.5" width="16" height="14.5" rx="2"/><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4"/>',
  quests: '<rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M8.3 9l1.6 1.6 2.9-3.1M14.6 9.3h1.6M8.3 14.6l1.6 1.6 2.9-3.1M14.6 14.9h1.6"/>',
  trial: '<path d="M7 3.5h10M7 20.5h10"/><path d="M8 3.5c0 4.2 4 5.2 4 8.5s-4 4.3-4 8.5M16 3.5c0 4.2-4 5.2-4 8.5s4 4.3 4 8.5"/>',
  map: '<path d="M12 21s-6.5-5.9-6.5-11a6.5 6.5 0 0 1 13 0c0 5.1-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.3"/>',
  codex: '<path d="M12 6.5C10 5 7.2 4.6 4 5v13.5c3.2-.4 6 0 8 1.5 2-1.5 4.8-1.9 8-1.5V5c-3.2-.4-6 0-8 1.5z"/><path d="M12 6.5V20"/>',
  mail: '<rect x="3.5" y="6" width="17" height="12.5" rx="1.5"/><path d="M4 7l8 6.2L20 7"/>',
  ranks: '<path d="M8 4h8v5.5a4 4 0 0 1-8 0z"/><path d="M8 5.5H5.2v1.2A3.2 3.2 0 0 0 8.4 10M16 5.5h2.8v1.2a3.2 3.2 0 0 1-3.2 3.3M12 13.5v3M8.5 20.5h7M10 20.5l.6-4h2.8l.6 4"/>',
  hall: '<path d="M6.5 20.5V5.2a1 1 0 0 1 .8-1l7.9-1.6a1 1 0 0 1 1.2 1v16.9"/><path d="M16.4 5h2.1v15.5M4 20.5h16"/><circle cx="13.6" cy="12.4" r="1" fill="currentColor" stroke="none"/>',
  shop: '<path d="M9 7.2h6l1.6-3.2H7.4z"/><path d="M9 7.2C5.6 9 4 12.2 4 15.2 4 18.8 7.6 21 12 21s8-2.2 8-5.8c0-3-1.6-6.2-5-8"/><circle cx="12" cy="14.6" r="2.2"/>',
  heroes: '<path d="M4.8 13.2a7.2 7.2 0 0 1 14.4 0v5.3a1.5 1.5 0 0 1-1.5 1.5h-3.2l-.9-4.3h-3.2l-.9 4.3H6.3a1.5 1.5 0 0 1-1.5-1.5z"/><path d="M5.2 11.4h13.6M12 6v5.4"/>',
  battle: '<path d="M4.5 4.5l10 10M19.5 4.5l-10 10M12 17l5.5-5.5M6.5 11.5L12 17M15.5 15.5l4 4M8.5 15.5l-4 4"/>',
  upgrades: '<rect x="5.5" y="3" width="13" height="18" rx="1.5"/><path d="M9 12.5l3-3 3 3M9 16.5l3-3 3 3"/>',
  events: '<path d="M6 21V3.5"/><path d="M6 4.5h12.5l-3.2 4.2 3.2 4.3H6"/>',
  lock: '<rect x="5.5" y="10.5" width="13" height="9.5" rx="1.5"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/>',
  unlock: '<rect x="5.5" y="10.5" width="13" height="9.5" rx="1.5"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 6.8-1.2"/>',
  check: '<path d="M5 12.5l4.2 4.2L19 7"/>',
  chevR: '<path d="M9.5 5.5L16 12l-6.5 6.5"/>',
  chevL: '<path d="M14.5 5.5L8 12l6.5 6.5"/>',
  chevD: '<path d="M5.5 9.5L12 16l6.5-6.5"/>',
  flame: '<path d="M12 21c-3.6 0-6-2.4-6-5.6 0-3.3 2.6-5 3.6-8.4 2.2 1.3 3 3.2 3 4.8 1-.6 1.6-1.8 1.7-3 2.2 1.8 3.7 4.1 3.7 6.6 0 3.2-2.4 5.6-6 5.6z"/><path d="M12 21c-1.5 0-2.6-1-2.6-2.5 0-1.6 1.3-2.4 2.6-4 1.3 1.6 2.6 2.4 2.6 4 0 1.5-1.1 2.5-2.6 2.5z"/>',
  chest: '<path d="M3.5 10.5h17v8.5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z"/><path d="M3.5 10.5c0-3.3 2-5.5 5-5.5h7c3 0 5 2.2 5 5.5"/><path d="M10.5 10.5v3h3v-3"/>',
  heart: '<path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.3a4.3 4.3 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z"/>',
  attack: '<path d="M19.5 4.5L9.5 14.5M19.5 4.5h-4.2M19.5 4.5v4.2M6.8 12.8l4.4 4.4M8.2 15.8L4.5 19.5"/>',
  reroll: '<path d="M19 9A7.5 7.5 0 0 0 5.8 7M5 15a7.5 7.5 0 0 0 13.2 2"/><path d="M19.5 4.5V9H15M4.5 19.5V15H9"/>',
  gift: '<rect x="4" y="9" width="16" height="11" rx="1.5"/><path d="M3.5 9h17M12 9v11M12 9c-1.5-3.5-5.5-4-5.5-1.5S10 9 12 9zM12 9c1.5-3.5 5.5-4 5.5-1.5S14 9 12 9z"/>',
  settings: '<path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/>',
  pencil: '<path d="M4.5 19.5l1-4 10-10 3 3-10 10z"/><path d="M13.5 7.5l3 3"/>',
  sun: '<circle cx="12" cy="12" r="3.6"/><path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4L18 18M6 18l1.6-1.6M16.4 7.6L18 6"/>',
  ultimate: '<path d="M12 3.5l2.2 5.3 5.8.5-4.4 3.8 1.3 5.6L12 15.8l-4.9 2.9 1.3-5.6L4 9.3l5.8-.5z"/>',
  basic: '<path d="M18.5 5.5L9 15M18.5 5.5l-.5 4M18.5 5.5l-4 .5M7 13l4 4M8.5 15.5l-3 3"/>',
  seat: '<path d="M7.5 11V5.5a1.5 1.5 0 0 1 1.5-1.5h6a1.5 1.5 0 0 1 1.5 1.5V11"/><path d="M5.5 11h13v3.5h-13zM7 14.5V20M17 14.5V20"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  minus: '<path d="M5 12h14"/>',
  close: '<path d="M6 6l12 12M18 6L6 18"/>',
  info: '<circle cx="12" cy="12" r="8.5"/><path d="M12 11v5.5M12 7.6v.2"/>',
  history: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
  infinity: '<path d="M6.5 8.5a3.5 3.5 0 1 0 0 7c2.6 0 4.4-2.6 5.5-3.5 1.1-.9 2.9-3.5 5.5-3.5a3.5 3.5 0 1 1 0 7c-2.6 0-4.4-2.6-5.5-3.5-1.1-.9-2.9-3.5-5.5-3.5z"/>',
  search: '<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5 5"/>',
  shield: '<path d="M12 3.5l7 2.5v5.2c0 4.3-3 7.4-7 8.8-4-1.4-7-4.5-7-8.8V6z"/>',
  sparkle: '<path d="M12 4l1.8 6.2L20 12l-6.2 1.8L12 20l-1.8-6.2L4 12l6.2-1.8z"/>',
  skull: '<path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l-2 1v2.5H8v-2.5z"/><circle cx="9.5" cy="12" r="1.3"/><circle cx="14.5" cy="12" r="1.3"/>',
  crown: '<path d="M4.5 17.5l-1-9 5 4 3.5-6 3.5 6 5-4-1 9z"/><path d="M5 20.5h14"/>',
  diamond: '<path d="M12 4l8 8-8 8-8-8z"/>',
  flag: '<path d="M6 21V4.5M6 5h10.5l-2 3.5 2 3.5H6"/>',
  swords: '<path d="M4.5 4.5l10 10M19.5 4.5l-10 10M12 17l5.5-5.5M6.5 11.5L12 17M15.5 15.5l4 4M8.5 15.5l-4 4"/>',
  dice: '<rect x="4.5" y="4.5" width="15" height="15" rx="3"/><circle cx="9" cy="9" r="1.1" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none"/><circle cx="15" cy="15" r="1.1" fill="currentColor" stroke="none"/>',
  retry: '<path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3"/><path d="M4.5 4.5v4h4"/>',
  back: '<path d="M14.5 5.5L8 12l6.5 6.5"/>',
  home: '<path d="M4 11.5L12 5l8 6.5"/><path d="M6.5 10v9.5h11V10"/><path d="M10 19.5v-5h4v5"/>',
  packs: '<rect x="8" y="3.5" width="11" height="15" rx="2"/><path d="M5.5 6.5v11.5a2 2 0 0 0 2 2H15"/><path d="M13.5 8.5l1.2 2.4 2.3.3-1.7 1.6.4 2.3-2.2-1.1-2.2 1.1.4-2.3-1.7-1.6 2.3-.3z"/>',
  film: '<rect x="3.5" y="5.5" width="17" height="13" rx="2"/><path d="M10 9.5v5l4.5-2.5z"/>'
};
export function icon(name, size, color, sw) {
  const s = size || 22;
  return svg('<svg class="bg-ico" width="' + s + '" height="' + s + '"' + svgSize(s) + ' viewBox="0 0 24 24" fill="none" stroke="' + (color || 'currentColor') +
    '" stroke-width="' + (sw || 1.5) + '" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + (P[name] || P.info) + '</svg>');
}
const STAR = 'M12 3.2l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.5l-5.2 2.8 1-5.8L3.5 9.4l5.9-.8z';
export function star(size, on) {
  return svg(on
    ? '<svg class="bg-star bg-star--on" width="' + size + '" height="' + size + '"' + svgSize(size) + ' viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR + '" fill="#D9B26A" stroke="#E6C88C" stroke-width="1" stroke-linejoin="round"/></svg>'
    : '<svg class="bg-star" width="' + size + '" height="' + size + '"' + svgSize(size) + ' viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR + '" fill="none" stroke="#5E6573" stroke-width="1.3" stroke-linejoin="round"/></svg>');
}
export function coin(size) {
  const s = size || 16;
  return svg('<svg class="bg-cur" width="' + s + '" height="' + s + '"' + svgSize(s) + ' viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="#D9B26A"/><circle cx="12" cy="12" r="9" fill="none" stroke="#F4E2B6" stroke-opacity=".5" stroke-width="1"/><rect x="8.6" y="8.6" width="6.8" height="6.8" fill="none" stroke="#6A5226" stroke-width="1.5"/></svg>');
}
export function crystal(size) {
  const s = size || 16;
  return svg('<svg class="bg-cur" width="' + s + '" height="' + s + '"' + svgSize(s) + ' viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.8l7.2 4.6v9.2L12 21.2l-7.2-4.6V7.4z" fill="#CF6F8C"/><path d="M12 2.8v18.4M4.8 7.4L12 12l7.2-4.6M4.8 16.6L12 12l7.2 4.6" fill="none" stroke="#FFE3EC" stroke-opacity=".55" stroke-width=".9"/></svg>');
}
export function ticket(size, color) {
  const s = size || 16, c = color || 'currentColor';
  return svg('<svg class="bg-cur" width="' + s + '" height="' + s + '"' + svgSize(s) + ' viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h14a1.5 1.5 0 0 1 1.5 1.5V10a2 2 0 0 0 0 4v2.5A1.5 1.5 0 0 1 19 18H5a1.5 1.5 0 0 1-1.5-1.5V14a2 2 0 0 0 0-4z" fill="none" stroke="' + c + '" stroke-width="1.5" stroke-linejoin="round"/><path d="M15 7v10" stroke="' + c + '" stroke-width="1.3" stroke-dasharray="1.5 1.7"/></svg>');
}
export function logoMark(size) {
  const s = size || 22;
  return svg('<svg width="' + s + '" height="' + s + '"' + svgSize(s) + ' viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l9.5 9.5-9.5 9.5L2.5 12z" fill="none" stroke="#D9B26A" stroke-width="1.4"/><path d="M12 7.5l4.5 4.5-4.5 4.5L7.5 12z" fill="#D9B26A"/></svg>');
}

/* ---------------- numbers ---------------- */
let NF = null;
export function fmt(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  try { NF = NF || new Intl.NumberFormat('en-US'); return NF.format(v); } catch (_) { return String(v); }
}
/* parts per million as a percent the way a player reads odds: 700000 → 70%, 9939 → 0.9939%, 1 → 0.0001% */
export function odds(ppm) {
  const v = Number(ppm) || 0;
  if (v <= 0) return '0%';
  return (v / 10000).toFixed(4).replace(/\.?0+$/, '') + '%';
}
export function plural(n, one, many) {
  if ((n | 0) === 1) return fmt(n) + ' ' + one;
  const auto = /o$/.test(one) ? one + 'es' : (/[^aeiou]y$/.test(one) ? one.slice(0, -1) + 'ies' : one + 's');
  return fmt(n) + ' ' + (many || auto);
}
/* a level's own name, or '' when it has none: Levels 11-99 are mostly named just
   "Level N", and printing that beside "Level N" read as a stutter */
export function levelOwnName(d, n) {
  const name = String((d && d.name) || '').trim();
  const lv = n != null ? n : (d && d.level);
  return name && name !== 'Level ' + lv ? name : '';
}
/* a boss name as it is drawn big (BGLAYOUT, 2026-09-24): "Leonorin Misthaven, the Sealed Court" is the
   name "Leonorin Misthaven" and the title "The Sealed Court"; a name without a comma is all name, and a
   title that only repeats the name ("Wynter, Wynter") is left out */
export function bossParts(full) {
  const s = String(full || '').trim(), i = s.indexOf(',');
  if (i <= 0) return { name: s, title: '' };
  const name = s.slice(0, i).trim(), t = s.slice(i + 1).trim();
  return { name: name, title: t && t.toLowerCase() !== name.toLowerCase() ? t.charAt(0).toUpperCase() + t.slice(1) : '' };
}
/* an enemy named inside a sentence: "the Horn Captain", "the Artificer", "Wynter", "Laith Reed" (a
   named boss goes by its name, without its title), or "the enemy" */
export function bossRef(name, start) {
  let s = String(name || '').trim();
  if (!s) return start ? 'The enemy' : 'the enemy';
  if (s.indexOf(',') > 0) return bossParts(s).name;
  if (/^the\s/i.test(s)) s = s.replace(/^the\s+/i, '');
  else if (!/\s/.test(s)) return s;
  return (start ? 'The ' : 'the ') + s;
}
/* a hero's short name: the name up to its first , ; : ( or spaced dash ("Ailuin, Archmage of…" is "Ailuin",
   not "Ailuin,"), each word's end punctuation dropped (an inner ' stays: "Jehn'to"), and a title keeps the
   word after it ("Sir Gawain", "Dr. Ada"); a caller may pass its own word for a name with nothing left */
const NAME_TITLES = new Set(['sir', 'dame', 'lord', 'lady', 'brother', 'sister', 'father', 'mother', 'captain', 'master', 'mistress',
  'doctor', 'dr', 'saint', 'st', 'king', 'queen', 'prince', 'princess', 'elder', 'madam', 'madame']);
export function firstName(name, fallback) {
  const raw = String(name || '').split(/[,;:(]|\s[-\u2010-\u2015]+(?:\s|$)/)[0].trim().split(/\s+/);
  const bare = w => w.replace(/^[^\p{L}\p{M}\p{N}]+|[^\p{L}\p{M}\p{N}]+$/gu, '');
  const words = raw.map(w => ({ w: bare(w), dot: /\.$/.test(w) })).filter(x => x.w);
  if (!words.length) return fallback == null ? 'Hero' : fallback;
  const a = words[0], b = words[1];
  return b && NAME_TITLES.has(a.w.toLowerCase()) ? a.w + (a.dot ? '.' : '') + ' ' + b.w : a.w;
}

/* ---------------- portraits (always square) ---------------- */
/* {img, name, rarity, size, level, radius, glow, eager, corrupted, alt, cls} */
/* The Hall's framing of a portrait (hohImgStyle in hoh-section-core.js): the saved focal point,
   0-100 on each axis (50 centred), is both object-position and the zoom origin, and the zoom is
   100-200. Out-of-range values fall back as the Hall's getters do. */
export function hallFrame(f) {
  const n = v => (v == null || v === '' ? NaN : Number(v));
  const x = n(f && f.x), y = n(f && f.y), z = n(f && f.zoom);
  return { x: x >= 0 && x <= 100 ? x : 50, y: y >= 0 && y <= 100 ? y : 50, zoom: z >= 100 && z <= 200 ? z : 100 };
}
export function frameStyle(f) {
  if (!f) return null;
  const g = hallFrame(f), pos = g.x + '% ' + g.y + '%';
  return { objectPosition: pos, transformOrigin: pos, scale: String(Math.round(g.zoom) / 100) };
}
/* 2026-09-28 (Jan: "take the design of the borders from the hall use that with the subtle glow and thickness do that
   across the battlegrounds menus"): every face wears the Hall gallery's rarity frame. A 2px ring in the tier colour
   (1.5px on Common and on faces under 40px) and the Hall's two-layer halo scaled to the face (--pg, 5-20px); a face
   drawn without glow (grids, rows, the top bar) keeps one tight halo, as the Hall's grid cards do. Mythic is the
   Hall's pink-to-teal foil, drawn OUTSIDE the face like every other tier's ring (a masked ::before), so a Mythic
   face is never bigger or smaller than its neighbours. Epic and up breathe on faces 72px and up (an opacity pulse on
   a layer of its own); a corrupted boss does not, its screen already carries the Hush's breath. A big Mythic's foil
   drifts slowly. Frame motion rests while a sheet or a pack covers the screen (html.bg-locked), and reduced motion
   keeps the frame and drops the motion. Injected once at the end of body, so it wins ties with the section's older
   .bg-por--t rules. */
const FRAME_CSS = '.bg-por{--pb:2px}.bg-por--small{--pb:1.5px}'
  + '.bg-por.bg-por--t0{box-shadow:0 0 0 1.5px rgba(138,128,144,.62)}'
  + '.bg-por.bg-por--t1{box-shadow:0 0 0 var(--pb) rgba(107,203,119,.74),0 0 var(--pg) rgba(107,203,119,.16)}'
  + '.bg-por.bg-por--t2{box-shadow:0 0 0 var(--pb) rgba(91,142,196,.84),0 0 var(--pg) rgba(70,130,221,.28),0 0 calc(var(--pg)*2.2) rgba(0,112,221,.12)}'
  + '.bg-por.bg-por--t3{--pc:rgba(180,107,255,.75);box-shadow:0 0 0 var(--pb) rgba(180,107,255,.86),0 0 var(--pg) rgba(163,53,238,.34),0 0 calc(var(--pg)*2.2) rgba(163,53,238,.15)}'
  + '.bg-por.bg-por--t4{--pc:rgba(255,190,90,.75);box-shadow:0 0 0 var(--pb) rgba(232,198,116,.9),0 0 calc(var(--pg)*1.15) rgba(255,180,70,.32),0 0 calc(var(--pg)*2.5) rgba(255,200,90,.15)}'
  + '.bg-por.bg-por--noglow.bg-por--t1{box-shadow:0 0 0 var(--pb) rgba(107,203,119,.74),0 0 calc(var(--pg)*.7) rgba(107,203,119,.14)}'
  + '.bg-por.bg-por--noglow.bg-por--t2{box-shadow:0 0 0 var(--pb) rgba(91,142,196,.84),0 0 calc(var(--pg)*.7) rgba(70,130,221,.22)}'
  + '.bg-por.bg-por--noglow.bg-por--t3{box-shadow:0 0 0 var(--pb) rgba(180,107,255,.86),0 0 calc(var(--pg)*.7) rgba(163,53,238,.26)}'
  + '.bg-por.bg-por--noglow.bg-por--t4{box-shadow:0 0 0 var(--pb) rgba(232,198,116,.9),0 0 calc(var(--pg)*.7) rgba(255,180,70,.24)}'
  + '.bg-por.bg-por--myth{--pc:rgba(239,148,255,.75);border:0;background:#0F1015;box-shadow:0 0 var(--pg) rgba(239,148,255,.36),0 0 calc(var(--pg)*2.2) rgba(179,245,238,.17)}'
  + '.bg-por.bg-por--myth.bg-por--noglow{box-shadow:0 0 calc(var(--pg)*.7) rgba(239,148,255,.22)}'
  + '.bg-por--myth::before{content:"";position:absolute;inset:calc(-1*var(--pb));padding:var(--pb);border-radius:calc(var(--r,8px) + var(--pb));pointer-events:none;'
  + 'background:linear-gradient(125deg,#ef94ff 0%,#b3f5ee 50%,#ef94ff 100%) 0% 50%/300% 300%;'
  + '-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;'
  + 'mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);mask-composite:exclude}'
  + '.bg-por.bg-por--corrupt{--pc:rgba(123,97,217,.8);box-shadow:0 0 0 var(--pb) var(--c-corrupt,#7B61D9),0 0 calc(var(--pg)*2) rgba(123,97,217,.5)}'
  + '.bg-por--myth.bg-por--corrupt::before{background:var(--c-corrupt,#7B61D9)}'
  + '.bg-por--big.bg-por--myth::before{animation:bgPorFoil 9s ease-in-out infinite}'
  /* 2026-09-30 (PERF-2026-09-30 item 1): a big mythic portrait's foil slides on its own layer. The ring is the same masked
     box as ::before; inside it the 300% gradient is one element moved by transform, exactly where background-position 0%
     to 100% put it (the picture is 3 boxes wide, so 100% is two boxes left: -66.667% of itself), so the browser composites
     it instead of repainting the ring every frame. ::before stays for every portrait without the layer. */
  + '.bg-por--foiled::before{display:none}'
  + '.bg-por__foil{position:absolute;inset:calc(-1*var(--pb));padding:var(--pb);border-radius:calc(var(--r,8px) + var(--pb));pointer-events:none;overflow:hidden;'
  + '-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;'
  + 'mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);mask-composite:exclude}'
  + '.bg-por__foil>i{position:absolute;left:0;top:-100%;width:300%;height:300%;background:linear-gradient(125deg,#ef94ff 0%,#b3f5ee 50%,#ef94ff 100%);'
  + 'transform:translateX(0);animation:bgPorFoilT 9s ease-in-out infinite}'
  + '.bg-por--corrupt .bg-por__foil>i{background:var(--c-corrupt,#7B61D9);animation:none}'
  + '@keyframes bgPorFoilT{0%,100%{transform:translateX(0)}50%{transform:translateX(-66.6667%)}}'
  + '.bg-por--breathe::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;'
  + 'box-shadow:0 0 calc(var(--pg)*1.8) 0 var(--pc);opacity:.3;animation:bgPorBreath 4.4s ease-in-out infinite}'
  /* the hero picker clips its text spans, and the portrait is a span too: its ring and halo stay outside */
  + '.bg-pick__item .bg-por{overflow:visible;max-width:none}'
  + 'html.bg-locked .bg-shell .bg-por,html.bg-locked .bg-shell .bg-por::before,html.bg-locked .bg-shell .bg-por::after,html.bg-locked .bg-shell .bg-por__foil>i{animation-play-state:paused}'
  + '@keyframes bgPorFoil{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}'
  + '@keyframes bgPorBreath{0%,100%{opacity:.28}50%{opacity:.78}}'
  + '@media (prefers-reduced-motion:reduce){.bg-por--big.bg-por--myth::before,.bg-por__foil>i,.bg-por--breathe::after{animation:none}.bg-por--breathe::after{opacity:.5}}'
  + 'html.bg-rm .bg-por--big.bg-por--myth::before,html.bg-rm .bg-por__foil>i,html.bg-rm .bg-por--breathe::after{animation:none}html.bg-rm .bg-por--breathe::after{opacity:.5}';
function frameCss() {
  if (document.getElementById('bg-frame-css') || !document.body) return;
  document.body.appendChild(h('style', { id: 'bg-frame-css', text: FRAME_CSS }));
}
/* 2026-09-30 (PERF-2026-09-30 item 3): a face no wider than its thumbnail's 320 px on this screen draws the stored thumbnail
   (bg-data hallFace records it by the full image): the same square crop, about a sixth of the pixels to fetch, decode and
   hold. A larger face keeps the full image, and so does a moving one (an animated upload's thumbnail is a still). */
function faceSrc(img, size, thumb) {
  try {
    const t = thumb || (window.__bgThumbOf && window.__bgThumbOf.get(img));
    if (!t || t === img || /\.(gif|webp|webm|mp4|mov|apng)(\?|#|$)/i.test(img)) return img;
    return (size || 60) * Math.max(1, window.devicePixelRatio || 1) <= 320 ? t : img;
  } catch (_) { return img; }
}
export function portrait(o) {
  frameCss();
  const size = o.size || 60, r = o.radius != null ? o.radius : 8;
  const tier = o.rarity == null ? null : rarityIndex(o.rarity);
  const myth = tier === 5 && o.foil !== false;
  const face = h('span', { class: 'bg-por__face', style: { width: px(size), height: px(size), borderRadius: px(r) } });
  if (o.img) {
    face.appendChild(h('img', { src: faceSrc(o.img, size, o.thumb), alt: o.alt != null ? o.alt : (o.name || ''), loading: o.eager ? 'eager' : 'lazy', decoding: 'async',
      fetchpriority: o.eager ? 'high' : null, width: size, height: size, draggable: 'false',
      class: o.corrupted ? 'bg-por__img bg-por__img--corrupt' : 'bg-por__img', style: o.frame ? frameStyle(o.frame) : null }));
  } else {
    /* R803: no picture yet shows a quiet silhouette, never a letter avatar */
    face.appendChild(h('span', { class: 'bg-por__initial', 'aria-hidden': 'true' }, icon('heroes', Math.max(16, Math.round(size * .42)), null, 1.4)));
  }
  if (o.corrupted) face.appendChild(h('span', { class: 'bg-por__corrupt', 'aria-hidden': 'true' }));
  if (o.level != null) face.appendChild(h('span', { class: 'bg-por__lv', text: String(o.level) }));
  const cls = ['bg-por'];
  if (o.cls) cls.push(o.cls);
  if (myth) cls.push('bg-por--myth');
  else if (tier != null) cls.push('bg-por--t' + tier);
  if (o.glow === false) cls.push('bg-por--noglow');
  if (o.corrupted) cls.push('bg-por--corrupt');
  if (size < 40) cls.push('bg-por--small');
  if (size >= 72) cls.push('bg-por--big');
  if (size >= 72 && o.glow !== false && !o.corrupted && tier != null && tier >= 3) cls.push('bg-por--breathe');
  const foil = myth && size >= 72;
  if (foil) cls.push('bg-por--foiled');
  const wrap = h('span', { class: cls.join(' '), style: { '--r': px(r), '--pg': px(Math.round(Math.max(5, Math.min(20, size * .11)))) } }, face);
  if (foil) wrap.insertBefore(h('span', { class: 'bg-por__foil', 'aria-hidden': 'true' }, h('i')), face);
  if (o.fav) wrap.appendChild(h('span', { class: 'bg-por__fav', 'aria-hidden': 'true' }, star(11, true)));
  wrap.style.borderRadius = px(r);
  return wrap;
}

/* ---------------- motion ---------------- */
/* reduced motion: the device's setting, or the page's own (Settings, Motion: bgw-motion, marked on <html>) */
export const MOTION_KEY = 'bgw-motion';
export function motionPref() { try { return localStorage.getItem(MOTION_KEY) === 'reduce' ? 'reduce' : 'device'; } catch (_) { return 'device'; } }
export function setMotionPref(v) {
  try { if (v === 'reduce') localStorage.setItem(MOTION_KEY, 'reduce'); else localStorage.removeItem(MOTION_KEY); } catch (_) {}
  document.documentElement.classList.toggle('bg-rm', v === 'reduce');
}
export function reduced() {
  if (motionPref() === 'reduce') return true;
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (_) { return false; }
}
/* one class drives each entrance; the CSS maps it to the reduced variant */
export function animateIn(node, kind) {
  if (!node) return node;
  const cls = 'bg-a-' + (kind || 'enter');
  node.classList.remove(cls);
  void node.offsetWidth;
  node.classList.add(cls);
  const done = () => { node.classList.remove(cls); node.removeEventListener('animationend', onEnd); };
  const onEnd = e => { if (e.target === node) done(); };
  node.addEventListener('animationend', onEnd);
  setTimeout(done, 900);
  return node;
}
/* leaves over ~160 ms and resolves once it is gone from the flow */
export function animateOut(node, kind) {
  return new Promise(resolve => {
    if (!node || !node.isConnected) { resolve(); return; }
    const cls = 'bg-a-' + (kind || 'leave');
    let finished = false;
    const done = () => { if (finished) return; finished = true; resolve(); };
    node.addEventListener('animationend', e => { if (e.target === node) done(); });
    node.classList.add(cls);
    setTimeout(done, 400);
  });
}
/* a height ease for a block that grows (the "see all" expansion) */
export function growFrom(node, fromHeight) {
  if (!node || reduced()) return;
  const to = node.scrollHeight;
  if (!(to > fromHeight)) return;
  node.style.overflow = 'hidden';
  node.style.height = fromHeight + 'px';
  node.style.transition = 'height 280ms cubic-bezier(.2,.7,.2,1)';
  requestAnimationFrame(() => { node.style.height = to + 'px'; });
  const end = () => { node.style.height = ''; node.style.overflow = ''; node.style.transition = ''; };
  node.addEventListener('transitionend', end, { once: true });
  setTimeout(end, 500);
}

/* ---------------- the sheet ---------------- */
/* sheet({title, sub, body: Node|Node[], actions: Node[], label, wide, list, onClose}) → {close, el, body} */
let SHEETS = 0;
const OPEN_SHEETS = new Set();
/* every open sheet closes (the router, when the browser goes Back or Forward under a sheet) */
export function closeSheets() { for (const s of Array.from(OPEN_SHEETS)) s.close(null); }
/* where focus goes when a sheet closes: what opened it, or, when a repaint replaced that control, the control that
   took its place (same data-fk), else the screen's heading; never the page body */
function refocus(prev, fk) {
  const ok = el => !!(el && el.isConnected && !el.disabled && el !== document.body && el.getClientRects().length);
  let el = ok(prev) ? prev : null;
  if (!el && fk) { try { el = document.querySelector('[data-fk="' + (window.CSS && CSS.escape ? CSS.escape(fk) : fk) + '"]'); } catch (_) { el = null; } if (!ok(el)) el = null; }
  if (!el && !document.querySelector('.bg-layer')) el = document.querySelector('[data-bg-panel]:not([hidden]) [data-bg-heading]');
  if (el) { try { el.focus({ preventScroll: true }); } catch (_) {} }
}
export function sheet(o) {
  const host = o.host || document.getElementById('bg-app') || document.body;
  const id = 'bg-sheet-' + (++SHEETS);
  const prevFocus = document.activeElement;
  const prevFk = prevFocus && prevFocus.getAttribute ? prevFocus.getAttribute('data-fk') : null;
  const scrim = h('div', { class: 'bg-scrim bg-a-fade' });
  const closeBtn = h('button', { type: 'button', class: 'bg-sheet__x', 'aria-label': 'Close' }, icon('close', 20));
  /* o.list: a list of choices (a picker's heroes, the History ledger) scrolls in its own region, the body's
     [data-bg-list] child, while the sheet's words and buttons stay in view (R802: no sheet scrolls to be read) */
  const body = h('div', { class: 'bg-sheet__body' + (o.list ? ' bg-sheet__body--list' : '') }, o.body || null);
  const panel = h('div', { class: 'bg-sheet' + (o.wide ? ' bg-sheet--wide' : '') + ' bg-a-sheet', role: 'dialog', 'aria-modal': 'true',
    'aria-labelledby': o.title ? id + '-h' : null, 'aria-label': o.title ? null : (o.label || 'Details'), tabindex: '-1' },
    h('div', { class: 'bg-sheet__grip', 'aria-hidden': 'true' }),
    o.title ? h('div', { class: 'bg-sheet__head' },
      h('div', { class: 'bg-sheet__titles' }, h('h2', { id: id + '-h', class: 'bg-sheet__title', text: o.title }), o.sub ? h('p', { class: 'bg-sheet__sub', text: o.sub }) : null),
      closeBtn) : closeBtn,
    body,
    o.actions && o.actions.length ? h('div', { class: 'bg-sheet__actions' }, o.actions) : null);
  const layer = h('div', { class: 'bg-layer', 'data-bg-sheet': o.name || '' }, scrim, panel);
  host.appendChild(layer);
  document.documentElement.classList.add('bg-locked');
  let closed = false;
  function close(result) {
    if (closed) return Promise.resolve();
    closed = true;
    OPEN_SHEETS.delete(api);
    document.removeEventListener('keydown', onKey, true);
    scrim.classList.remove('bg-a-fade'); panel.classList.remove('bg-a-sheet');
    const out = Promise.all([animateOut(scrim, 'fadeout'), animateOut(panel, 'sheetout')]);
    return out.then(() => {
      layer.remove();
      if (!document.querySelector('.bg-layer')) document.documentElement.classList.remove('bg-locked');
      /* a sheet that opens another as it closes (a picker, then its class) hands focus on itself */
      if (!document.querySelector('.bg-layer,.bg-reveal') || (prevFocus && prevFocus.isConnected)) refocus(prevFocus, prevFk);
      if (o.onClose) { try { o.onClose(result); } catch (_) {} }
    });
  }
  function onKey(e) {
    /* Nested sheets are deliberate (for example: choose a hero, then choose an unresolved class).
       Only the top dialog may consume Escape or trap Tab; otherwise one key closes every layer. */
    const layers = document.querySelectorAll('.bg-layer');
    if (layers.length && layers[layers.length - 1] !== layer) return;
    if (e.key === 'Escape') { e.preventDefault(); close(null); }
    else if (e.key === 'Tab') {
      const f = [...panel.querySelectorAll('button:not([disabled]),a[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter(x => x.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  }
  document.addEventListener('keydown', onKey, true);
  scrim.addEventListener('click', () => close(null));
  closeBtn.addEventListener('click', () => close(null));
  requestAnimationFrame(() => { try { if (!closed) panel.focus({ preventScroll: true }); } catch (_) {} });
  const api = { close: close, el: panel, body: body, layer: layer };
  OPEN_SHEETS.add(api);
  return api;
}

/* ---------------- a live countdown (R803) ----------------
   A used-up daily thing says on the control itself when it comes back ("Next ticket in 5h 12m"), never a silent
   disable. countdown(node, at, words, onDone): node's text is words(left) and follows the clock (every 15 s) while
   the node is on the page; onDone runs once, when the count reaches zero on screen (a time already past shows
   words('') and calls nothing, so a stale server time never loops a refresh). */
export function untilWords(ms) {
  const m = Math.max(1, Math.ceil(ms / 60000)), hh = Math.floor(m / 60), mm = m % 60;
  return hh ? hh + 'h' + (mm ? ' ' + mm + 'm' : '') : mm + 'm';
}
export function countdown(node, at, words, onDone) {
  const end = new Date(at).getTime();
  if (!node) return node;
  if (!isFinite(end) || end <= Date.now()) { node.textContent = words(''); return node; }
  let timer = 0, seen = false;
  const tick = () => {
    if (node.isConnected) seen = true;
    else if (seen || timer === -1) { clearInterval(timer); return; }
    const left = end - Date.now();
    if (left <= 0) { clearInterval(timer); timer = -1; node.textContent = words(''); if (onDone) onDone(); return; }
    node.textContent = words(untilWords(left));
  };
  tick();
  timer = setInterval(tick, 15000);
  return node;
}

/* ---------------- toast ---------------- */
let toastNode = null, toastTimer = null;
export function toast(text, kind) {
  const host = document.getElementById('bg-app') || document.body;
  if (toastNode) { toastNode.remove(); toastNode = null; }
  clearTimeout(toastTimer);
  toastNode = h('div', { class: 'bg-toast bg-a-toast' + (kind === 'error' ? ' bg-toast--error' : ''), role: kind === 'error' ? 'alert' : 'status' },
    kind === 'error' ? icon('info', 18) : icon('check', 18), h('span', { text: text }));
  host.appendChild(toastNode);
  const node = toastNode;
  toastTimer = setTimeout(() => { animateOut(node, 'fadeout').then(() => { node.remove(); if (toastNode === node) toastNode = null; }); }, kind === 'error' ? 4200 : 2600);
}

/* ---------------- the favorite star (hoh_bgw_card_favorite) ---------------- */
/* a toggle: aria-pressed and the words Favorite / Favorited; `compact` keeps only the star
   (its name goes in the label), for a card corner */
export function favButton(on, o) {
  const opt = o || {};
  const b = h('button', { type: 'button', class: 'bg-fav' + (opt.compact ? ' bg-fav--compact' : '') + (opt.cls ? ' ' + opt.cls : ''), 'data-bg-action': 'favorite' });
  if (opt.fk) b.setAttribute('data-fk', opt.fk);
  setFav(b, on, opt.name);
  if (typeof opt.onToggle === 'function') b.addEventListener('click', e => { e.stopPropagation(); opt.onToggle(b); });
  return b;
}
export function setFav(b, on, name) {
  if (!b) return;
  if (name != null) b.dataset.name = String(name);
  const who = b.dataset.name ? ' ' + b.dataset.name : '';
  b.classList.toggle('is-on', !!on);
  b.setAttribute('aria-pressed', on ? 'true' : 'false');
  b.setAttribute('aria-label', (on ? 'Favorited' : 'Favorite') + who);
  b.replaceChildren(star(b.classList.contains('bg-fav--compact') ? 18 : 20, !!on), b.classList.contains('bg-fav--compact') ? '' : h('span', { class: 'bg-fav__t', text: on ? 'Favorited' : 'Favorite' }));
}

/* ---------------- coins that fly to a counter (the daily gift) ---------------- */
/* CSS keyframes (bgCoinFly) carry each coin, so the motion is read from the computed style.
   onLand(i, n) runs as each coin arrives; the promise settles after the last. Reduced motion:
   no coins, it settles at once with false. */
export function flyCoins(from, to, o) {
  const opt = o || {};
  if (reduced() || !from || !to || !from.isConnected || !to.isConnected) return Promise.resolve(false);
  const a = from.getBoundingClientRect(), b = to.getBoundingClientRect();
  const n = Math.max(1, Math.min(12, opt.count || 8));
  const x0 = a.left + a.width / 2, y0 = a.top + a.height / 2;
  const x1 = b.left + Math.min(22, b.width / 2), y1 = b.top + b.height / 2;
  const host = document.getElementById('bg-app') || document.body;
  let landed = 0;
  return new Promise(resolve => {
    const done = () => { if (landed >= n) return; landed = n; resolve(true); };
    for (let i = 0; i < n; i++) {
      const jx = (Math.random() - .5) * Math.min(90, a.width * .7), jy = (Math.random() - .5) * 14;
      const el = h('span', { class: 'bg-flycoin', 'aria-hidden': 'true', style: {
        left: (x0 + jx - 9) + 'px', top: (y0 + jy - 9) + 'px', '--dx': (x1 - x0 - jx) + 'px', '--dy': (y1 - y0 - jy) + 'px',
        '--wx': ((Math.random() - .5) * 120) + 'px', animationDelay: (i * 55) + 'ms' } }, coin(18));
      el.addEventListener('animationend', () => {
        el.remove();
        landed++;
        try { if (typeof opt.onLand === 'function') opt.onLand(landed, n); } catch (_) {}
        if (landed >= n) resolve(true);
      }, { once: true });
      host.appendChild(el);
    }
    setTimeout(done, 1800 + n * 60);
  });
}

/* ---------------- particles: subtle, capped, never under reduced motion ---------------- */
export const PALETTE = Object.freeze({
  common: ['#CFC7DA', '#A9A2BD'], uncommon: ['#7EE6A0', '#BDF5CF'], rare: ['#8FC0FF', '#CFE2FF'], epic: ['#D6A4FF', '#B06AFF'],
  legendary: ['#FFD27A', '#FFB84A', '#FFF0C8'], mythic: ['#EF94FF', '#B3F5EE', '#FFFFFF'], gold: ['#E6C88C', '#D9B26A', '#FFF0C8'],
  ember: ['#F2C9A2', '#E0925A']
});
const FX = { canvas: null, ctx: null, parts: [], raf: 0, last: 0 };
const FX_CAP = 90;
function fxCanvas() {
  if (FX.canvas && FX.canvas.isConnected) return FX.canvas;
  const c = h('canvas', { class: 'bg-fx', 'aria-hidden': 'true' });
  (document.getElementById('bg-app') || document.body).appendChild(c);
  FX.canvas = c; FX.ctx = c.getContext('2d');
  return c;
}
function fxSize() {
  const c = FX.canvas, dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = innerWidth, hgt = innerHeight;
  if (c.width !== Math.round(w * dpr) || c.height !== Math.round(hgt * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(hgt * dpr); }
  FX.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
function fxTick(t) {
  const dt = Math.min(40, t - (FX.last || t)); FX.last = t;
  const g = FX.ctx;
  fxSize();
  g.clearRect(0, 0, innerWidth, innerHeight);
  FX.parts = FX.parts.filter(p => (p.life -= dt) > 0);
  for (const p of FX.parts) {
    p.vy += p.grav * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vr * dt;
    const a = Math.max(0, Math.min(1, p.life / p.max * 1.4));
    g.save(); g.globalAlpha = a; g.translate(p.x, p.y); g.rotate(p.rot);
    g.fillStyle = p.color; g.shadowColor = p.color; g.shadowBlur = 6;
    g.fillRect(-p.s / 2, -p.s / 2, p.s, p.s);
    g.restore();
  }
  if (FX.parts.length) FX.raf = requestAnimationFrame(fxTick);
  else { FX.raf = 0; FX.last = 0; g.clearRect(0, 0, innerWidth, innerHeight); }
}
/* burst(at: Element|{x,y}, {palette, count, spread, rise}) */
/* 2026-09-28 (soak S1: the phone's Back mid-fight or on a result left the Battlegrounds, which read as a crash). While a
   full-screen moment is up (a battle, a result), one history entry stands for it and Back calls its handler instead of
   leaving the page. Releasing it consumes the entry; a new moment registered while that Back is still travelling waits
   for it, then pushes its own. The URL never changes, so the router sees the same route and does nothing. */
const BACK = { fn: null, pushed: false, popping: false, queued: false };
function pushBack() { try { history.pushState({ bgGuard: 1 }, '', location.href); BACK.pushed = true; } catch (_) {} }
export function guardBack(fn) {
  BACK.fn = fn;
  if (BACK.pushed) return;
  if (BACK.popping) { BACK.queued = true; return; }
  pushBack();
}
export function releaseBack() {
  BACK.fn = null; BACK.queued = false;
  if (!BACK.pushed || BACK.popping) return;
  BACK.popping = true;
  try { history.back(); } catch (_) { BACK.popping = false; }
}
if (typeof window !== 'undefined') window.addEventListener('popstate', () => {
  if (BACK.popping) {
    BACK.popping = false; BACK.pushed = false;
    if (BACK.queued && BACK.fn) { BACK.queued = false; pushBack(); }
    return;
  }
  if (BACK.pushed && !(history.state && history.state.bgGuard)) {
    BACK.pushed = false;
    const f = BACK.fn; BACK.fn = null;
    if (typeof f === 'function') { try { f(); } catch (_) {} }
  }
});
export function burst(at, opts) {
  if (reduced()) return;
  /* a control a repaint has already taken away has no place on screen (its box reads 0,0): no burst from the corner */
  if (at && at.nodeType === 1 && !at.isConnected) return;
  const o = opts || {};
  let x = 0, y = 0;
  if (at && typeof at.getBoundingClientRect === 'function') { const r = at.getBoundingClientRect(); x = r.left + r.width / 2; y = r.top + r.height / 2; }
  else if (at) { x = at.x; y = at.y; }
  fxCanvas();
  const pal = PALETTE[o.palette] || o.palette || PALETTE.gold;
  const n = Math.min(o.count || 16, Math.max(0, FX_CAP - FX.parts.length));
  const spread = o.spread || 1;
  for (let i = 0; i < n; i++) {
    const ang = Math.random() * Math.PI * 2, sp = (0.05 + Math.random() * 0.16) * spread;
    const life = 520 + Math.random() * 420;
    FX.parts.push({ x: x, y: y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - (o.rise || 0.06), grav: 0.00022, rot: Math.random() * 3, vr: (Math.random() - .5) * .01,
      s: 2 + Math.random() * 2.6, color: pal[i % pal.length], life: life, max: life });
  }
  if (!FX.raf) FX.raf = requestAnimationFrame(fxTick);
}
export function fxLive() { return FX.parts.length; }

/* ---------------- small shared pieces ---------------- */
export function pill(node, value, label, attrs) {
  return h('span', Object.assign({ class: 'bg-pill', role: 'group', 'aria-label': label }, attrs || {}), node, h('span', { class: 'bg-pill__v' }, value));
}
export function bar(fraction, cls, label) {
  const f = Math.max(0, Math.min(1, Number(fraction) || 0));
  return h('span', { class: 'bg-bar ' + (cls || ''), role: 'img', 'aria-label': label || '' },
    h('span', { class: 'bg-bar__fill', style: { width: (f * 100).toFixed(1) + '%' } }));
}
export function stars(n, size, label) {
  const on = Math.max(0, Math.min(3, n | 0));
  return h('span', { class: 'bg-stars', role: 'img', 'aria-label': label || (on + ' of 3 stars') }, star(size || 18, on >= 1), star(size || 18, on >= 2), star(size || 18, on >= 3));
}
export function spinner() { return h('span', { class: 'bg-spin', 'aria-hidden': 'true' }); }
export function skeletonRow(n, size) {
  const out = [];
  for (let i = 0; i < n; i++) out.push(h('span', { class: 'bg-skel', style: { width: px(size), height: px(size) }, 'aria-hidden': 'true' }));
  return out;
}
/* a button that shows a spinner while its promise runs, and can never be double tapped */
/* a server action on a button (R803, Jan: "people scroll away" while a tiny icon spins). The tap answers in the same
   frame (the button presses in and springs back, a reward sends a few sparks off it), a sheen keeps sweeping the
   button and its spinner turns for as long as the server works, and a reward lands with a pop and a burst.
   Steps that chain on one button (load the battle, check the server, start the level) read as one wait: the
   button stays busy between them. opts.reward marks a payout (a claim, a pack, a merge, a reroll, a purchase). */
export function busy(button, promise, opts) {
  if (!button) return promise;
  const o = opts || {};
  button.__busyN = (button.__busyN | 0) + 1;
  if (button.__busyOff) { cancelAnimationFrame(button.__busyOff); button.__busyOff = 0; }
  if (!button.classList.contains('is-busy')) {
    button.classList.remove('is-tapped', 'is-landed');
    void button.offsetWidth;
    button.classList.add('is-tapped');
    if (o.reward) burst(button, { palette: 'gold', count: 8, spread: .5 });
  }
  /* a focused button that turns busy is disabled, and the browser drops the focus to the page body: once the work ends
     it goes back to the button, or to what a repaint put in its place (same data-fk), else the screen's heading. Only
     while the body still has it and nothing above the button (a sheet, a pack, a result, the fight) has taken over */
  if (document.activeElement === button && !button.__busyFocus) button.__busyFocus = { fk: button.getAttribute('data-fk') };
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.classList.add('is-busy');
  const done = ok => {
    button.__busyN = Math.max(0, (button.__busyN | 0) - 1);
    if (button.__busyN) return;
    /* a payout lands at once, where the button still stands (the screen may redraw right after) */
    if (ok && o.reward && button.isConnected) { burst(button, { palette: 'gold', count: 14 }); button.classList.add('is-landed'); }
    /* one frame of grace, so the next chained step keeps the same wait */
    button.__busyOff = requestAnimationFrame(() => {
      button.__busyOff = 0;
      if (button.__busyN) return;
      button.disabled = false; button.removeAttribute('aria-busy'); button.classList.remove('is-busy', 'is-tapped');
      const bf = button.__busyFocus;
      button.__busyFocus = null;
      if (!bf || (document.activeElement && document.activeElement !== document.body)) return;
      const over = Array.from(document.querySelectorAll('.bg-layer,.bg-reveal,.bg-result,#hoh-contract-overlay,[data-bg-cutscene],[data-bg-title]')).pop();
      if (!over || over.contains(button)) refocus(button, bf.fk);
    });
  };
  return Promise.resolve(promise).then(v => { done(true); return v; }, e => { done(false); throw e; });
}
